import React, {useEffect, useRef} from 'react';
import {useEditor, EditorContent} from '@tiptap/react';

import {buildSceneExtensions, parseSceneDoc} from './tiptapExtensions';
import {computeCounts} from './sceneCommentMapping';
import SceneToolbar from './sceneToolbar';

/**
 * Edits a single scene: one ProseMirror doc (scene.content JSON string) with a
 * tiptap editor and the inline formatting toolbar. Emits
 * onChange(sceneJsonString, {wordCount, charCount, paragraphCount}) on every
 * edit. Used by SceneChapterEditor, one instance per scene.
 */
const SceneEditor = ({scene, editable = true, onChange, autoFocus = false}) => {
  // Keep the latest onChange in a ref so the editor's onUpdate closure (created
  // once per scene) always calls the current handler.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const editor = useEditor(
    {
      extensions: buildSceneExtensions(),
      content: parseSceneDoc(scene.content),
      editable,
      autofocus: autoFocus ? 'end' : false,
      onUpdate: ({editor: activeEditor}) => {
        const doc = activeEditor.getJSON();
        const counts = computeCounts(doc);
        if (onChangeRef.current) {
          onChangeRef.current(JSON.stringify(doc), {
            wordCount: counts.wordCount,
            charCount: counts.charCount,
            paragraphCount: counts.paragraphCount,
            text: counts.text
          });
        }
      }
    },
    // Recreate the editor when we switch to a different scene identity.
    [scene.id]
  );

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  return (
    <div className="scene-editor">
      {editable && <SceneToolbar editor={editor} />}
      <EditorContent editor={editor} className="scene-editor-content br-text" />
    </div>
  );
};

export default SceneEditor;
